import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, Info, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BulkImportCustomers
 * ----------------------------------------------------------------------------
 * Upload an .xlsx of PPPoE customers, pick owner+branch+plan+due-date, optionally
 * backfill an invoice+payment record for each row, then commit.
 *
 * Workflow:
 *   1. User picks file -> POST /api/v2/customers/bulk-import-preview -> show summary.
 *   2. User picks dropdowns (owner/branch/plan/due-date) -> POST commit.
 *
 * Auto-detects column names so it works for ROFEK-style and RIFAI-style sheets.
 */
const BulkImportCustomers = () => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { total_rows, conflict_count, mapping, sample }
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  // Dropdown data
  const [branches, setBranches] = useState([]);
  const [owners, setOwners] = useState([]);
  const [plans, setPlans] = useState([]);
  const [routers, setRouters] = useState([]);

  // Form state
  const [branchId, setBranchId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [planId, setPlanId] = useState('');
  const [routerId, setRouterId] = useState(''); // '' means NULL (no router)
  const [dueDateDay, setDueDateDay] = useState(10);
  const [nextDueDate, setNextDueDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setDate(10);
    return d.toISOString().slice(0, 10);
  });
  const [backfillInvoice, setBackfillInvoice] = useState(true);
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  // 2026-07-17: connection_type auto-preset dari URL query `?type=hotspot|pppoe`
  // Ketika di-preset (user datang dari halaman Hotspot atau PPPoE), kotak
  // sebaliknya akan di-DISABLE agar tidak salah pilih.
  const [searchParams] = useSearchParams();
  const _presetType = (searchParams.get('type') || '').toLowerCase();
  const isTypeLocked = _presetType === 'hotspot' || _presetType === 'pppoe';
  const [connectionType, setConnectionType] = useState(isTypeLocked ? _presetType : 'pppoe');

  // ===== Fetch dropdown data =====
  useEffect(() => {
    (async () => {
      try {
        const [bRes, oRes, pRes, rRes] = await Promise.all([
          axios.get(`${API_URL}/api/branches`, { headers }),
          axios.get(`${API_URL}/api/v2/owners?limit=500`, { headers }),
          axios.get(`${API_URL}/api/v2/service-plans?limit=500`, { headers }),
          axios.get(`${API_URL}/api/v2/mpanel/available-routers`, { headers }),
        ]);
        setBranches(bRes.data?.data || []);
        setOwners(oRes.data?.data || oRes.data?.owners || []);
        setPlans(pRes.data?.data || pRes.data?.service_plans || pRes.data || []);
        const rPayload = rRes.data;
        setRouters(Array.isArray(rPayload) ? rPayload : (rPayload?.data || rPayload?.routers || []));
      } catch (e) {
        console.error('load dropdowns failed', e);
        toast.error('Gagal memuat data cabang/owner/paket.');
      }
    })();
    // eslint-disable-line
  }, []);

  // Cascading owners + routers based on branch
  const visibleOwners = useMemo(() => {
    if (!branchId) return owners;
    return owners.filter(o => String(o.branch_id || '') === String(branchId));
  }, [owners, branchId]);

  const visibleRouters = useMemo(() => {
    if (!branchId) return routers;
    return routers.filter(r => String(r.branch_id || '') === String(branchId));
  }, [routers, branchId]);

  // ===== Preview =====
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await axios.post(`${API_URL}/api/v2/customers/bulk-import-preview`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      toast.success(`Berhasil parse ${res.data.total_rows} baris dari Excel`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal parse Excel');
    } finally {
      setPreviewing(false);
    }
  };

  // ===== Cancel / Reset =====
  // Wipes file, preview, result, and resets form fields to safe defaults so the
  // user can start over with a different Excel.
  const onReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setBranchId('');
    setOwnerId('');
    setPlanId('');
    setRouterId('');
    // Reset the <input type="file"> visually too.
    const fileInput = document.querySelector('[data-testid="bulk-import-file-input"]');
    if (fileInput) fileInput.value = '';
    toast.info('Form di-reset. Silakan pilih file Excel baru.');
  };

  // ===== Commit =====
  const onCommit = async () => {
    if (!file) return toast.error('Pilih file Excel dulu');
    if (!branchId) return toast.error('Pilih cabang');
    if (!ownerId) return toast.error('Pilih owner');
    if (!planId) return toast.error('Pilih paket');
    if (!nextDueDate) return toast.error('Pilih tanggal jatuh tempo bulan berjalan');

    const dupMsg = preview?.conflict_count
      ? ` (${preview.conflict_count} username bentrok akan di-auto-rename dengan suffix angka)`
      : '';
    if (!window.confirm(
      `Akan diimport ${preview?.total_rows || 0} baris${dupMsg}. Lanjut?`
    )) return;

    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('owner_id', ownerId);
      fd.append('branch_id', branchId);
      fd.append('service_plan_id', planId);
      fd.append('due_date_day', String(dueDateDay));
      fd.append('next_due_date', nextDueDate);
      if (routerId) fd.append('router_id', routerId);
      fd.append('backfill_invoice', backfillInvoice ? 'true' : 'false');
      if (backfillInvoice && paymentDate) fd.append('payment_date', paymentDate);
      fd.append('connection_type', connectionType);

      const res = await axios.post(`${API_URL}/api/v2/customers/bulk-import-commit`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setResult(res.data);
      toast.success(`Berhasil! ${res.data.inserted} customer baru, ${res.data.invoices_backfilled} invoice paid.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal commit import');
    } finally {
      setCommitting(false);
    }
  };

  const mapping = preview?.mapping || {};
  const hasMapping = (key) => !!mapping[key];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="bulk-import-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
          Import Pelanggan dari Excel
        </h1>
        <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">
          Upload file <code>.xlsx</code> berisi data PPPoE, lalu pilih konfigurasi cabang/owner/paket.
          Format kolom yang dikenali otomatis:&nbsp;
          <code>Login, Password, FullName/nama, Phone/telp, Address/alamat, Latitude+Longitude</code> atau&nbsp;
          <code>kordinat (lat,lon)</code>.
        </p>
      </div>

      {/* ---- Step 1: Upload ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <span className="bg-emerald-100 text-emerald-700 w-7 h-7 inline-flex items-center justify-center rounded-full text-xs font-bold dark:bg-emerald-900/40 dark:text-emerald-300">1</span>
            Pilih File Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="file"
            accept=".xlsx,.xls"
            onChange={onPickFile}
            disabled={previewing || committing}
            data-testid="bulk-import-file-input"
          />
          {previewing && <p className="text-sm mt-2 text-gray-500 flex items-center gap-1 dark:text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Memproses file...</p>}
        </CardContent>
      </Card>

      {/* ---- Step 2: Preview ---- */}
      {preview && (
        <Card data-testid="bulk-import-preview-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-700 w-7 h-7 inline-flex items-center justify-center rounded-full text-xs font-bold dark:bg-emerald-900/40 dark:text-emerald-300">2</span>
              Hasil Parse
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded">
                <div className="text-xs text-gray-500 dark:text-slate-400">Total Baris</div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{preview.total_rows}</div>
              </div>
              <div className={`p-3 rounded ${preview.conflict_count > 0 ? 'bg-amber-50 dark:bg-amber-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
                <div className="text-xs text-gray-500 dark:text-slate-400">Duplikat (auto-rename)</div>
                <div className={`text-2xl font-bold ${preview.conflict_count > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>{preview.conflict_count}</div>
                {preview.conflict_count > 0 && <div className="text-[10px] text-amber-600 mt-1 dark:text-amber-400">akan diberi suffix angka</div>}
              </div>
              <div className="bg-emerald-50 p-3 rounded dark:bg-emerald-900/30">
                <div className="text-xs text-gray-500 dark:text-slate-400">Akan di-insert</div>
                <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{preview.total_rows}</div>
              </div>
              <div className="bg-slate-50 p-3 rounded dark:bg-slate-900">
                <div className="text-xs text-gray-500 dark:text-slate-400">Kolom Terdeteksi</div>
                <div className="text-sm font-semibold flex flex-wrap gap-1 mt-1">
                  {['login','password','name','phone','email','address','latitude','longitude','coordinate','account_number','package','registration_date','isolir_date'].map(k => (
                    <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] ${hasMapping(k) ? 'bg-green-200 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-200 text-gray-500 dark:bg-slate-700 dark:text-slate-400 line-through'}`}>{k}</span>
                  ))}
                </div>
              </div>
            </div>

            {preview.sample?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1 dark:text-slate-400">Contoh 3 baris pertama:</p>
                <div className="overflow-x-auto border rounded">
                  <table className="text-xs w-full">
                    <thead className="bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="p-1.5 text-left">Login</th><th className="p-1.5 text-left">Nama</th>
                        <th className="p-1.5 text-left">Phone</th>
                        <th className="p-1.5 text-left">Email</th>
                        <th className="p-1.5 text-left">Tgl Reg</th>
                        <th className="p-1.5 text-left">Tgl Isolir</th>
                        <th className="p-1.5 text-left">Koordinat</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.slice(0,3).map((r,i) => (
                        <tr key={i} className="border-t">
                          <td className="p-1.5 font-mono">{r.login}</td>
                          <td className="p-1.5">{r.name}</td>
                          <td className="p-1.5">{r.phone || '-'}</td>
                          <td className="p-1.5">{r.email || '-'}</td>
                          <td className="p-1.5">{r.registration_date || '-'}</td>
                          <td className="p-1.5">{r.isolir_date || '-'}</td>
                          <td className="p-1.5">{r.latitude && r.longitude ? `${r.latitude}, ${r.longitude}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.conflict_count > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded text-xs flex items-start gap-2 dark:bg-amber-900/30">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">{preview.conflict_count} pppoe_username bentrok dengan data lama.</span> Otomatis akan diberi suffix angka (mis. <code>santi</code> → <code>santi2</code>). Data lama tidak diubah.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Step 3: Konfigurasi + Commit ---- */}
      {preview && preview.total_rows > 0 && (
        <Card data-testid="bulk-import-config-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span className="bg-emerald-100 text-emerald-700 w-7 h-7 inline-flex items-center justify-center rounded-full text-xs font-bold dark:bg-emerald-900/40 dark:text-emerald-300">3</span>
              Konfigurasi Import
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Tipe Koneksi Pelanggan *{isTypeLocked && <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">(terkunci — datang dari halaman {connectionType === 'hotspot' ? 'Hotspot' : 'PPPoE'})</span>}</Label>
              <div className="flex gap-3 mt-1">
                <button
                  type="button"
                  disabled={isTypeLocked && connectionType !== 'pppoe'}
                  onClick={() => { if (!isTypeLocked) setConnectionType('pppoe'); }}
                  data-testid="bulk-conn-pppoe"
                  className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition text-left ${
                    connectionType === 'pppoe'
                      ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : (isTypeLocked
                          ? 'border-slate-200 bg-slate-100 text-slate-400 opacity-50 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500'
                          : 'border-slate-300 bg-white hover:border-blue-400 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200')
                  }`}
                >
                  <div className="font-semibold">PPPoE (Pelanggan Bulanan)</div>
                  <div className="text-xs opacity-75 mt-0.5">Auth via /ppp/secret MikroTik · Framed-Protocol=PPP</div>
                </button>
                <button
                  type="button"
                  disabled={isTypeLocked && connectionType !== 'hotspot'}
                  onClick={() => { if (!isTypeLocked) setConnectionType('hotspot'); }}
                  data-testid="bulk-conn-hotspot"
                  className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition text-left ${
                    connectionType === 'hotspot'
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : (isTypeLocked
                          ? 'border-slate-200 bg-slate-100 text-slate-400 opacity-50 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500'
                          : 'border-slate-300 bg-white hover:border-emerald-400 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200')
                  }`}
                >
                  <div className="font-semibold">Hotspot (Pelanggan WiFi)</div>
                  <div className="text-xs opacity-75 mt-0.5">Auth via /ip/hotspot/user · tanpa Framed-Protocol</div>
                </button>
              </div>
            </div>
            <div>
              <Label>Cabang *</Label>
              <Select value={branchId} onValueChange={(v) => { setBranchId(v); setOwnerId(''); setRouterId(''); }}>
                <SelectTrigger data-testid="bulk-branch-select"><SelectValue placeholder="Pilih cabang..." /></SelectTrigger>
                <SelectContent>
                  {branches.map(b => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Owner *</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger data-testid="bulk-owner-select"><SelectValue placeholder="Pilih owner..." /></SelectTrigger>
                <SelectContent>
                  {visibleOwners.map(o => (
                    <SelectItem key={o.id} value={String(o.id)}>
                      {o.owner_code || o.code ? `${o.owner_code || o.code} — ` : ''}{o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Paket Layanan *</Label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger data-testid="bulk-plan-select"><SelectValue placeholder="Pilih paket..." /></SelectTrigger>
                <SelectContent>
                  {plans.map(p => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name} — Rp {Number(p.price || 0).toLocaleString('id-ID')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Router (opsional)</Label>
              <Select value={routerId || 'none'} onValueChange={(v) => setRouterId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="bulk-router-select"><SelectValue placeholder="Tidak di-link ke router" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Tidak di-link ke router —</SelectItem>
                  {visibleRouters.map(r => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.name || r.router_name} ({r.host || r.ip_address || '-'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tanggal Jatuh Tempo (hari) *</Label>
              <Input
                type="number" min={1} max={28}
                value={dueDateDay}
                onChange={(e) => setDueDateDay(parseInt(e.target.value || '10', 10))}
                data-testid="bulk-duedateday-input"
              />
              <p className="text-xs text-gray-500 mt-0.5 dark:text-slate-400">Misal 10 = jatuh tempo tanggal 10 setiap bulan</p>
            </div>
            <div>
              <Label>Next Due Date (bulan ini/depan) *</Label>
              <Input
                type="date"
                value={nextDueDate}
                onChange={(e) => setNextDueDate(e.target.value)}
                data-testid="bulk-nextdue-input"
              />
            </div>
            <div className="md:col-span-2 border-t pt-3 mt-1">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="backfill" checked={backfillInvoice}
                  onCheckedChange={(v) => setBackfillInvoice(!!v)}
                  data-testid="bulk-backfill-checkbox"
                />
                <Label htmlFor="backfill" className="cursor-pointer font-medium">
                  Backfill Invoice + Payment (semua langsung LUNAS)
                </Label>
              </div>
              {backfillInvoice && (
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-xs">Tanggal pembayaran:</Label>
                  <Input
                    type="date" value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-44"
                    data-testid="bulk-paydate-input"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 dark:text-slate-400">
                <Info className="w-3 h-3" /> Jika dicentang, tiap customer langsung dapat 1 invoice + 1 payment record untuk bulan tersebut.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action */}
      {preview && preview.total_rows > 0 && (
        <div className="flex justify-end gap-2">
          <Button
            onClick={onReset}
            disabled={committing}
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
            data-testid="bulk-import-cancel-btn"
          >
            <X className="w-4 h-4 mr-1" /> Batal
          </Button>
          <Button
            onClick={onCommit}
            disabled={committing || !branchId || !ownerId || !planId}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bulk-import-commit-btn"
          >
            {committing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Mengimport...</> :
              <><Upload className="w-4 h-4 mr-1" /> Import {preview.total_rows} Pelanggan</>}
          </Button>
        </div>
      )}

      {/* Result */}
      {result && (
        <Card className="border-emerald-300" data-testid="bulk-import-result-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="w-5 h-5" /> Import Selesai
            </CardTitle>
            <Button
              variant="ghost" size="sm"
              onClick={onReset}
              className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 h-8 w-8 p-0 dark:text-slate-400 dark:hover:bg-slate-700"
              data-testid="bulk-import-close-result-btn"
              title="Tutup & reset form"
            >
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              <li><b>Owner:</b> {result.owner}</li>
              <li><b>Cabang:</b> {result.branch}</li>
              <li><b>Paket:</b> {result.plan}</li>
              <li><b>Customer ter-insert:</b> {result.inserted}</li>
              <li><b>Auto-rename (bentrok username):</b> {result.renamed_count || 0}</li>
              <li><b>Gagal/skip:</b> {result.skipped}</li>
              <li><b>Invoice + Payment backfilled:</b> {result.invoices_backfilled}</li>
              <li><b>Total pendapatan (jika backfill):</b> Rp {Number(result.total_amount || 0).toLocaleString('id-ID')}</li>
            </ul>
            {result.renamed_samples && result.renamed_samples.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs dark:bg-amber-900/30">
                <div className="font-medium text-amber-800 mb-1">Contoh username yang di-rename:</div>
                <ul className="space-y-0.5 font-mono">
                  {result.renamed_samples.map((r, i) => (
                    <li key={i}><code>{r.original}</code> &rarr; <code className="text-amber-700 font-bold dark:text-amber-300">{r.final}</code></li>
                  ))}
                  {result.renamed_count > result.renamed_samples.length && (
                    <li className="text-amber-600 italic dark:text-amber-400">...dan {result.renamed_count - result.renamed_samples.length} username lain</li>
                  )}
                </ul>
              </div>
            )}
            <div className="mt-4 pt-3 border-t flex justify-end gap-2">
              <Button
                onClick={onReset}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                data-testid="bulk-import-newimport-btn"
              >
                <Upload className="w-4 h-4 mr-1" /> Import File Baru
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkImportCustomers;
